import { DownloadDto, HighscoreDto } from "@citychallenge/api-public";
import { Environment } from "../../environment";
import { observable } from "mobx";
import { Loadable } from "../../loadable";
import { ApiService } from "../api";
import { FileService } from "../file";
import { createContext } from "react";
import { GameReleaseData } from "@citychallenge/common";
import { AuthService } from "../auth";
import { ServiceInterface } from "../service.interface";
import { DeviceService } from "../device";

export const GameServiceContext = createContext<GameService>(null as any);

export class GameService implements ServiceInterface {
  public context = GameServiceContext;
  constructor(
    private apiService: ApiService,
    private fileService: FileService,
    private authService: AuthService,
    private deviceService: DeviceService
  ) {}

  @observable
  public highscores: { [key: string]: Loadable<HighscoreDto[]> } = {};

  public async downloadUrl(dto: DownloadDto) {
    const promise = this.authService.isLoggedIn
      ? this.apiService.clientPrivate.downloadUrl(dto)
      : this.apiService.clientPublic.downloadUrlPublic(dto);
    const { data } = await promise;
    return data;
  }

  /**
   * `divisionId` is the project the story is played in. The server applies that
   * project's authentication rules to the leaderboard. It needs the project
   * for organization stories, which have no project of their own.
   */
  public loadableHighscores(
    gameId: string,
    sessionId?: string,
    userKey?: string,
    divisionId?: string
  ) {
    const cacheId = `${gameId}${sessionId ? sessionId : ""}${
      userKey ? userKey : ""
    }${divisionId ? divisionId : ""}`;
    if (this.highscores[cacheId]) return this.highscores[cacheId];
    const loadable = Loadable.create(() =>
      this.fetchHighscores(gameId, sessionId, userKey, divisionId)
    );
    this.highscores[cacheId] = loadable;
    return loadable;
  }

  public async fetchHighscores(
    gameId: string,
    sessionId?: string,
    userKey?: string,
    divisionId?: string
  ): Promise<HighscoreDto[]> {
    if (userKey) {
      const { data } =
        await this.apiService.clientPublic.getJoinedHighscoresWithKey(
          gameId,
          userKey,
          divisionId
        );
      return data;
    }
    if (sessionId) {
      const { data } =
        await this.apiService.clientPublic.getHighscoresWithSession(
          gameId,
          sessionId,
          divisionId
        );
      return data;
    }

    const { data } = await this.apiService.clientPublic.getHighscores(
      gameId,
      divisionId
    );

    return data;
  }

  private downloadTasks: { [key: string]: Promise<string> | undefined } = {};

  public async downloadGameFile(
    release: GameReleaseData,
    bundleFile: string,
    pathResolver?: (path: string) => string
  ) {
    const taskId = `${release.game.id}/${release.releaseVersion}/${bundleFile}`;
    const existingTask = this.downloadTasks[taskId];

    if (existingTask) {
      return await existingTask;
    }

    try {
      const task = this.createGameDownloadTask(
        release,
        bundleFile,
        pathResolver
      );
      this.downloadTasks[taskId] = task;
      return await task;
    } finally {
      delete this.downloadTasks[taskId];
    }
  }

  private async createGameDownloadTask(
    release: GameReleaseData,
    bundleFile: string,
    pathResolver?: (path: string) => string
  ) {
    const { fileService } = this;
    const bundleDirectory = fileService.resolveRelativeBundlePath(
      release.game.id,
      release.releaseVersion
    );
    const downloadPath = `${bundleDirectory}/${bundleFile}`;

    if (await fileService.exists(downloadPath)) {
      return fileService.readFile(downloadPath);
    }

    await fileService.mkdir(bundleDirectory);

    const { bundlePath } = await this.downloadUrl({
      gameId: release.game.id,
      releaseVersion: release.releaseVersion,
      appVersion: Environment.version,
    });

    const downloadUrl = `${bundlePath}/${bundleFile}`;

    const resolvedUrl = pathResolver ? pathResolver(downloadUrl) : downloadUrl;

    const { promise } = fileService.downloadFile(resolvedUrl, downloadPath);

    await promise;

    return fileService.readFile(downloadPath);
  }

  public resolvePreferredLanguage(
    release: { languages?: { id: string; name: string }[] },
    forcedLanguage: string | undefined
  ) {
    // 1. First check if any particular language needs to be forced
    if (forcedLanguage) {
      const found = release.languages?.find((l) => l.id === forcedLanguage);
      if (found) {
        return found.id;
      }
    }

    const deviceLanguages = this.deviceService.getPreferredLanguages() || [];

    // 2. Otherwise we give preference to the order or languages in the users device
    if (deviceLanguages.length) {
      for (const { full, short } of deviceLanguages) {
        const found =
          release.languages?.find((l) => l.id === full) ||
          release.languages?.find((l) => l.id.split("-")[0] === short);

        if (found) {
          return found.id;
        }
      }
    }

    // 3. In all cases we fall back to either English or the first language in the list
    const fallBackLocale =
      release.languages?.find((l) => l.id === "en")?.id ||
      release.languages?.[0]?.id;

    return fallBackLocale || null;
  }

  public resolveBundleFile(
    release: { languages?: { id: string; name: string }[] },
    forcedLanguage: string | undefined
  ) {
    const pref = this.resolvePreferredLanguage(release, forcedLanguage);
    return pref ? `game.${pref}.json` : "game.json";
  }
}
